import { jwtDecode } from 'jwt-decode'
import type { User } from 'oidc-client-ts'
import { useCallback, useEffect, useRef } from 'react'
import { useAuth } from 'react-oidc-context'
import { getAuthority } from '../contexts/auth'
import type { organization } from '../types/oidcUser'
import { useExchangeToken } from './apiHooks/tokenValidationHooks'

const { VITE_COMPANION_CLIENT_ID } = import.meta.env

// Custom JWT payload interface extending the standard JwtPayload
interface CustomJwtPayload {
  sub?: string
  email?: string
  name?: string
  firstName?: string
  lastName?: string
  organization?: organization[]
  role?: string
  exp?: number
  scope?: string
}

// Native bridge configuration interface
interface NativeAuthConfig {
  accessToken?: string
  idToken?: string | null
  platform?: string
  oidcUser?: {
    access_token?: string
    id_token?: string | null
  }
  timestamp?: number
}

/**
 * Hook to handle authentication via native bridge from macOS app
 * macOS app calls: window.setAuthTokens({ accessToken, idToken?, platform })
 * or window.setAuthTokens({ oidcUser, platform })
 */
export const useNativeBridgeAuth = () => {
  const hasProcessed = useRef(false)
  const auth = useAuth()
  // const { mutateAsync: validateToken } = useValidateToken();
  const { mutateAsync: exchangeToken } = useExchangeToken()

  // helper to process tokens
  const processTokens = useCallback(
    async (accessToken: string, idToken: string | null) => {
      if (hasProcessed.current) return
      hasProcessed.current = true

      // validate token format (basic check for JWT structure)
      if (!accessToken.includes('.') || accessToken.length < 20) {
        console.error('Invalid access token format')
        return
      }

      try {
        const { access_token, refresh_token } = await exchangeToken(accessToken)

        if (access_token) {
          const userData = jwtDecode<CustomJwtPayload>(access_token)

          // Create a partial User object with the required properties
          const oidcUser = {
            access_token: access_token,
            id_token: idToken || undefined,
            token_type: 'Bearer',
            refresh_token,
            profile: {
              sub: userData.sub || '',
              email: userData.email || '',
              name: userData.name || '',
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              organization: userData.organization || [],
              role: userData.role,
              // Add required IdTokenClaims properties
              iss: '',
              aud: '',
              exp: userData.exp || Math.floor(Date.now() / 1000) + 3600,
              iat: Math.floor(Date.now() / 1000),
            },
            expires_at: userData.exp,
            scope:
              userData.scope ||
              'openid profile offline_access napsterai.companions.identity.main',
            // Add required User properties with sensible defaults
            session_state: null,
            state: null,
            expires_in: userData.exp
              ? userData.exp - Math.floor(Date.now() / 1000)
              : 3600,
            expired: false,
            scopes:
              userData.scope ||
              'openid profile offline_access napsterai.companions.identity.main',
            toStorageString: function () {
              return JSON.stringify(this)
            },
          }

          // check both URL param and localStorage for platform
          const platform =
            window.location.search.includes('platform') ||
            localStorage.getItem('napster_platform') !== null

          const LSKey = `oidc.user:${getAuthority()}:${
            VITE_COMPANION_CLIENT_ID as string
          }`

          localStorage.setItem(LSKey, JSON.stringify(oidcUser))

          await auth.events.load(oidcUser as User)

          console.log(
            `✅ Authentication set via native bridge for platform: ${platform}${
              idToken ? ' (with id_token)' : ''
            }`,
          )

          // clean up native bridge data from window
          delete window.__NATIVE_AUTH__
        }
      } catch (error) {
        console.error('❌ Error processing token authentication:', error)
        hasProcessed.current = false
      }
    },
    [exchangeToken, auth.events],
  )

  useEffect(() => {
    // only process once per component lifecycle
    if (hasProcessed.current) return

    // helper to handle auth data
    const handleAuthData = (nativeData: NativeAuthConfig) => {
      if (nativeData.platform) {
        localStorage.setItem('napster_platform', nativeData.platform)
      }

      // extract access_token from either format
      const accessToken =
        nativeData.oidcUser?.access_token || nativeData.accessToken
      const idToken = nativeData.oidcUser?.id_token || nativeData.idToken

      if (!accessToken) {
        console.error('No access token provided')
        return
      }

      void processTokens(accessToken, idToken || null)
    }

    // check if auth data was already provided (before React loaded)
    if (window.__NATIVE_AUTH__) {
      handleAuthData(window.__NATIVE_AUTH__ as NativeAuthConfig)
      return
    }

    // listen for native bridge event (if called after React loads)
    const handleNativeAuth = (event: CustomEvent) => {
      if (!hasProcessed.current && event.detail) {
        handleAuthData(event.detail as NativeAuthConfig)
      }
    }

    window.addEventListener(
      'nativeAuthReady',
      handleNativeAuth as EventListener,
    )

    return () => {
      window.removeEventListener(
        'nativeAuthReady',
        handleNativeAuth as EventListener,
      )
    }
  }, [processTokens])
}
